import React, { useEffect, useMemo, useState } from 'react';
import { Boxes, CheckCircle2, AlertCircle, Plus, Trash2 } from 'lucide-react';
import {
  SOURCE_SYSTEMS,
  MOUNT_METHODS,
  findMountMethod,
  type CrmAssignment,
  type CrmEntry,
  type RdsDatabase,
} from '@atlas/shared';
import { SourceBadge } from '../components/SourceBadge.js';
import { useAuth } from '../auth/AuthProvider.js';
import { getCrmConfig, setCrmConfig } from '../lib/callables.js';

type SaveState =
  | { status: 'idle' }
  | { status: 'saving' }
  | { status: 'ok' }
  | { status: 'error'; message: string };

const EMPTY_ASSIGNMENT: CrmAssignment = { methodId: null, databaseId: null };

/** Generates a custom CRM id that doesn't collide with built-ins or existing ones. */
function newCrmId(existing: CrmEntry[]): string {
  const taken = new Set<string>([
    ...SOURCE_SYSTEMS,
    ...existing.map((c) => c.id),
  ]);
  let n = existing.length + 1;
  let id = `custom-${n}`;
  while (taken.has(id)) {
    n += 1;
    id = `custom-${n}`;
  }
  return id;
}

export function CrmsPage() {
  const { user, userRecord } = useAuth();

  // The infrastructure's mount databases, read-only: the target selector.
  const [databases, setDatabases] = useState<RdsDatabase[]>([]);
  const [crms, setCrms] = useState<CrmEntry[]>([]);
  const [assignments, setAssignments] = useState<Record<string, CrmAssignment>>({});
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [save, setSave] = useState<SaveState>({ status: 'idle' });

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await getCrmConfig();
      setDatabases(result.data.config.databases);
      setCrms(result.data.config.crms ?? []);
      setAssignments(result.data.config.assignments ?? {});
      setUpdatedBy(result.data.updatedBy);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load CRM config.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const databaseById = useMemo(
    () => new Map(databases.map((d) => [d.id, d])),
    [databases],
  );

  if (!userRecord?.isAdmin) {
    return (
      <div className="p-8 text-sm text-[#73726e]">You don't have admin access.</div>
    );
  }

  function markDirty() {
    setSave({ status: 'idle' });
  }

  function updateAssignment(crm: string, patch: Partial<CrmAssignment>) {
    setAssignments((a) => ({
      ...a,
      [crm]: { ...(a[crm] ?? EMPTY_ASSIGNMENT), ...patch },
    }));
    markDirty();
  }

  function addCrm() {
    setCrms((cs) => [...cs, { id: newCrmId(cs), name: '' }]);
    markDirty();
  }

  function renameCrm(id: string, name: string) {
    setCrms((cs) => cs.map((c) => (c.id === id ? { ...c, name } : c)));
    markDirty();
  }

  /**
   * The id is the slug the rest of the system stores — it keys the assignment
   * and becomes the leading folder of the S3 path for a Redshift export — so
   * changing it has to carry the assignment across rather than orphan it.
   */
  function setCrmId(oldId: string, rawId: string) {
    const id = rawId.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '_');
    setCrms((cs) => cs.map((c) => (c.id === oldId ? { ...c, id } : c)));
    setAssignments((a) => {
      if (!(oldId in a) || oldId === id) return a;
      const next = { ...a };
      next[id] = next[oldId] ?? EMPTY_ASSIGNMENT;
      delete next[oldId];
      return next;
    });
    markDirty();
  }

  function removeCrm(id: string) {
    setCrms((cs) => cs.filter((c) => c.id !== id));
    setAssignments((a) => {
      const next = { ...a };
      delete next[id];
      return next;
    });
    markDirty();
  }

  async function handleSave() {
    setSave({ status: 'saving' });
    try {
      const result = await setCrmConfig({ config: { crms, assignments } });
      setDatabases(result.data.config.databases);
      setCrms(result.data.config.crms ?? []);
      setAssignments(result.data.config.assignments ?? {});
      setUpdatedBy(user?.email ?? updatedBy);
      setSave({ status: 'ok' });
    } catch (err) {
      setSave({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to save.',
      });
    }
  }

  function assignmentRow(
    crmId: string,
    labelCell: React.ReactNode,
    withTopBorder: boolean,
    onRemove?: () => void,
  ) {
    const assignment = assignments[crmId] ?? EMPTY_ASSIGNMENT;
    const methodKnown =
      assignment.methodId === null ||
      findMountMethod(assignment.methodId) !== undefined;
    const dbKnown =
      assignment.databaseId === null || databaseById.has(assignment.databaseId);
    const method = assignment.methodId
      ? findMountMethod(assignment.methodId)
      : undefined;

    return (
      <div
        key={crmId}
        className={`flex items-start gap-3 px-4 py-2.5 ${
          withTopBorder ? 'border-t border-[#f1f1ef]' : ''
        }`}
      >
        <div className="flex-1 pt-1.5">{labelCell}</div>

        <div className="w-52 flex flex-col gap-1">
          <select
            className="w-full px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] bg-white"
            value={methodKnown ? assignment.methodId ?? '' : ''}
            onChange={(e) =>
              updateAssignment(crmId, {
                methodId: e.target.value === '' ? null : e.target.value,
              })
            }
          >
            <option value="">No method</option>
            {MOUNT_METHODS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
          {method && (
            <span className="text-[10px] text-[#aeaaa4] leading-tight">
              {method.description}
            </span>
          )}
        </div>

        <div className="w-52">
          <select
            className="w-full px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] bg-white disabled:bg-[#f7f7f5] disabled:text-[#aeaaa4]"
            value={dbKnown ? assignment.databaseId ?? '' : ''}
            disabled={databases.length === 0}
            onChange={(e) =>
              updateAssignment(crmId, {
                databaseId: e.target.value === '' ? null : e.target.value,
              })
            }
          >
            <option value="">No database</option>
            {databases.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </div>

        <div className="w-8 flex justify-end pt-1.5">
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="text-[#aeaaa4] hover:text-[#e03e3e] transition-colors"
              title="Remove CRM"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-8 py-8">
      <div className="flex items-center gap-2 mb-1">
        <Boxes size={16} className="text-[#73726e]" />
        <h1 className="text-lg font-semibold text-[#37352f]">CRMs</h1>
      </div>
      <p className="text-sm text-[#73726e] mb-6">
        For each CRM, choose how its export is processed (the mount method) and
        which RDS database it lands in. The databases themselves are listed on
        the <span className="font-medium">RDS databases</span> tab.
      </p>

      {loadError && (
        <p className="text-xs text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2 mb-4">
          {loadError}
        </p>
      )}

      {loading ? (
        <div className="text-sm text-[#aeaaa4]">Loading…</div>
      ) : (
        <>
          {databases.length === 0 && (
            <p className="text-xs text-[#d9730d] bg-[#fef3e5] border border-[#f5d9b0] rounded px-3 py-2 mb-4">
              No RDS databases defined yet. Add one on the RDS databases tab before
              assigning targets.
            </p>
          )}

          <section className="mb-4">
            <div className="border border-[#e9e9e7] rounded-lg overflow-hidden">
              {/* Header row */}
              <div className="flex items-center gap-3 px-4 py-2 bg-[#fafafa] border-b border-[#e9e9e7]">
                <span className="flex-1 text-[10px] font-semibold uppercase tracking-wider text-[#aeaaa4]">
                  CRM
                </span>
                <span className="w-52 text-[10px] font-semibold uppercase tracking-wider text-[#aeaaa4]">
                  Method
                </span>
                <span className="w-52 text-[10px] font-semibold uppercase tracking-wider text-[#aeaaa4]">
                  Target database
                </span>
                <span className="w-8" />
              </div>

              {SOURCE_SYSTEMS.map((crm, i) =>
                assignmentRow(
                  crm,
                  <SourceBadge system={crm} />,
                  i !== 0,
                ),
              )}

              {crms.map((c) =>
                assignmentRow(
                  c.id,
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus={c.name === ''}
                      className="flex-1 px-2.5 py-1 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
                      placeholder="CRM name"
                      value={c.name}
                      onChange={(e) => renameCrm(c.id, e.target.value)}
                    />
                    <input
                      className="w-40 px-2.5 py-1 text-sm font-mono border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
                      placeholder="slug"
                      title="Used in S3 paths and stored on every run. Lowercase letters, numbers, _ and - only."
                      value={c.id}
                      onChange={(e) => setCrmId(c.id, e.target.value)}
                    />
                  </div>,
                  true,
                  () => removeCrm(c.id),
                ),
              )}
            </div>
          </section>

          <div className="mb-8">
            <button
              type="button"
              onClick={addCrm}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#37352f] border border-[#e9e9e7] rounded hover:bg-[#f1f1ef] transition-colors"
            >
              <Plus size={14} />
              Add CRM
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={save.status === 'saving'}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-[#37352f] rounded hover:bg-[#2e2e2c] disabled:opacity-50 transition-colors"
            >
              {save.status === 'saving' ? 'Saving…' : 'Save'}
            </button>
            {save.status === 'ok' && (
              <span className="flex items-center gap-1.5 text-xs text-[#0f7b6c]">
                <CheckCircle2 size={13} /> Saved.
              </span>
            )}
            {save.status === 'error' && (
              <span className="flex items-center gap-1.5 text-xs text-[#e03e3e]">
                <AlertCircle size={13} /> {save.message}
              </span>
            )}
            {updatedBy && save.status !== 'error' && (
              <span className="text-[10px] text-[#aeaaa4] ml-auto">
                Last changed by {updatedBy}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
